/**
 * Dinosaur stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const dinosaurStickersPack: ToilePack = {
    slug: 'dinosaur-stickers',
    category: 'sticker',
    name: 'Dinosaur stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'trex', label: 'T-rex', subject: 'a round T-rex with tiny arms and a big grin'},
        {key: 'longneck', label: 'Long neck', subject: 'a long-necked dinosaur nibbling a leaf from a tall tree'},
        {key: 'triceratops', label: 'Triceratops', subject: 'a triceratops with three horns and a friendly face'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a stegosaurus instead', 'for a lunchbox', 'as a baby version'],
    directionHint: 'try: a stegosaurus instead',
    seo: {
        title: 'Dinosaur Stickers: T-Rex, Long Neck, Triceratops',
        metaDescription: 'Nine dinosaur stickers for the lunchbox — a T-rex, a long-neck, a triceratops — in kawaii, bold cartoon, and hand-drawn styles.',
        h1: 'Dinosaur stickers: T-rex, long-neck, triceratops',
        tagline: 'Nine stickers, three lunchbox dinosaurs, three styles each.',
        intro: 'The dinosaur for the lunchbox: a round T-rex with tiny arms and a big grin, a long-necked dinosaur nibbling a leaf, and a friendly-faced triceratops with three horns. Each rendered kawaii, bold cartoon, and loose hand-drawn. Ask for a different dinosaur, a volcano scene, or a whole prehistoric composition, and the same three styles carry it.',
        useCases: [
            'a lunchbox or water bottle',
            "a kids' bedroom wall",
            'a school folder or notebook',
            'a birthday party favour bag',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine dinosaur stickers — a T-rex, a long-necked dinosaur, and a triceratops — in kawaii, bold cartoon, and hand-drawn finishes.'},
            {q: 'Can I get a different dinosaur, like a stegosaurus?', a: 'Yes — the direction field takes any variation, including a different dinosaur species, a volcano scene, or a whole prehistoric composition.'},
            {q: 'Are these scientifically accurate dinosaurs?', a: 'No — these are rounded, friendly cartoon characters made for stickers, not paleontological illustrations.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
        ],
    },
}
